import { useEffect, useRef } from "react";
import { OTP_LENGTH } from "../constants";
import styles from "../twoFactor.module.css";

// 6 single-digit boxes: auto-advance, paste fills all, backspace/arrow navigation.
// After an error, focus returns to the first box and the next keypress clears all digits.
const OtpInput = ({ value, onChange, hasError, onComplete, disabled }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (hasError) inputRefs.current[0]?.focus();
  }, [hasError]);

  const update = (next) => {
    onChange(next);
    if (next.every(Boolean)) onComplete?.(next.join(""));
  };

  const handleChange = (event, index) => {
    const digit = event.target.value.replace(/\D/g, "").slice(-1);
    if (!digit) return;

    const base = hasError ? Array(OTP_LENGTH).fill("") : [...value];
    const position = hasError ? 0 : index;
    base[position] = digit;
    update(base);

    if (position < OTP_LENGTH - 1) inputRefs.current[position + 1]?.focus();
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = [...value];
      if (next[index]) {
        next[index] = "";
        onChange(next);
      } else if (index > 0) {
        next[index - 1] = "";
        onChange(next);
        inputRefs.current[index - 1]?.focus();
      }
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const digits = event.clipboardData
      .getData("Text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH)
      .split("");
    if (!digits.length) return;
    const next = Array(OTP_LENGTH)
      .fill("")
      .map((_, i) => digits[i] || "");
    update(next);
    inputRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
  };

  return (
    <div className={`flex justify-between gap-2.5 ${hasError ? styles.shake : ""}`}>
      {value.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${index + 1}`}
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          className={`min-w-0 flex-1 max-w-[56px] h-14 rounded-xl text-center font-geomanistMedium text-2xl outline-none transition-colors
            border-[1.5px]
            ${
              hasError
                ? "border-failed bg-[#FFF5F6] text-failed"
                : digit
                  ? "border-blueish bg-[#F3F7FE] text-blackShade"
                  : "border-[#D3D4DA] text-blackShade"
            }
            focus:border-blueish focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,97,211,0.12)]`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
